/**
 * Adaptive Pong opponent. Easy must actually miss; skill climbs with rallies
 * but stays inside a difficulty band so Easy never becomes a wall.
 *
 * Soft is a mild handicap, not a corpse — floor stays high enough to
 * reach far shots; missChance handles the real Easy giveaways.
 */

import type { Difficulty } from './difficulty'

export interface AiTuning {
  /** 0 = gentle, 1 = sharp. */
  skill: number
  maxSpeed: number
  aimError: number
  /** Fraction of court the ball must pass before AI commits (higher = lazier). */
  commitX: number
  /** Chance to aim slightly wrong on purpose. */
  fluff: number
  /** Chance to take a full dive at the wrong height (real miss). */
  missChance: number
  /** How hard the paddle chases the target (1 = full). */
  chase: number
}

export class AdaptivePongAi {
  skill = 0.36
  private band = { min: 0.24, max: 0.78 }
  private playerHits = 0
  private playerGoals = 0
  private aiGoals = 0
  private rally = 0

  setDifficulty(d: Difficulty) {
    if (d === 'easy') {
      // Starts near Warming up; Soft floor still covers the court.
      this.skill = 0.28
      this.band = { min: 0.22, max: 0.46 }
    } else if (d === 'hard') {
      this.skill = 0.56
      this.band = { min: 0.4, max: 0.94 }
    } else {
      this.skill = 0.36
      this.band = { min: 0.26, max: 0.78 }
    }
    this.playerHits = 0
    this.playerGoals = 0
    this.aiGoals = 0
    this.rally = 0
  }

  onPlayerHit() {
    this.rally += 1
    this.playerHits += 1
    this.nudge(0.012 + Math.min(0.02, this.rally * 0.0015))
  }

  /** Human scored (AI missed). */
  onPlayerGoal() {
    this.playerGoals += 1
    this.rally = 0
    this.nudge(-0.02)
  }

  /** AI scored (human missed). */
  onAiGoal() {
    this.aiGoals += 1
    this.rally = 0
    const lead = this.aiGoals - this.playerGoals
    // If AI is stomping, soften so Easy stays playable.
    if (lead >= 1) this.nudge(-0.06)
    else this.nudge(0.02)
  }

  private nudge(delta: number) {
    this.skill = Math.max(this.band.min, Math.min(this.band.max, this.skill + delta))
  }

  tuning(): AiTuning {
    const s = this.skill
    return {
      skill: s,
      maxSpeed: 170 + s * 230,
      aimError: 58 - s * 48,
      commitX: 0.48 + (1 - s) * 0.18,
      fluff: Math.max(0.08, 0.42 - s * 0.38),
      missChance: Math.max(0.03, 0.24 - s * 0.26),
      chase: 0.58 + s * 0.42
    }
  }

  label(): string {
    const s = this.skill
    if (s < 0.26) return 'Soft'
    if (s < 0.42) return 'Warming up'
    if (s < 0.58) return 'Even'
    if (s < 0.74) return 'Sharp'
    return 'Hot'
  }
}
